/**
 * The optics of a backlit panel.
 *
 * The first version of this feature cut a groove in the back of a panel and
 * called the millimetre of plastic above it a "diffuser". That models a
 * channel, not light: at that distance an LED strip reads as a row of dots, and
 * no slider can change it. What actually decides whether a backlit panel looks
 * like a glowing surface or a string of bulbs is distance — between the emitters
 * and the surface, and between one run of strip and the next.
 *
 * Both rules below are rules of thumb from lightbox practice rather than a
 * radiometric simulation. They are stated as ratios, with the number that would
 * fix them, because that is what the person choosing an air gap needs.
 */

/**
 * The common strip types, with the dimensions a channel has to be cut to.
 *
 * A strip is not a flat tape. The emitters are packages sitting on top of it,
 * and they are both taller and narrower than the tape they are on — which is
 * why a groove sized for the tape alone does not fit the strip. The numbers
 * are the usual package sizes: 5050 for the sparser strips, 2835 for the
 * denser one, and a COB's phosphor band, which barely stands proud at all.
 */
export interface LedType {
  id: string;
  label: string;
  /** Distance between emitters. COB strips are effectively continuous. */
  pitchMm: number;
  /** Rough power draw at full white, for the wiring notes. */
  wattsPerMetre: number;
  /** How far the emitters stand above the tape, in millimetres. */
  emitterMm: number;
  /** Width of the emitter package, in millimetres. */
  emitterWidthMm: number;
}

export const LED_TYPES: readonly LedType[] = [
  {
    id: 'cob',
    label: 'COB — continuous',
    pitchMm: 2,
    wattsPerMetre: 12,
    emitterMm: 0.4,
    emitterWidthMm: 6,
  },
  {
    id: 'd120',
    label: '120 LEDs/m',
    pitchMm: 8.3,
    wattsPerMetre: 14,
    emitterMm: 0.8,
    emitterWidthMm: 3.5,
  },
  {
    id: 'd60',
    label: '60 LEDs/m',
    pitchMm: 16.7,
    wattsPerMetre: 7,
    emitterMm: 1.6,
    emitterWidthMm: 5,
  },
  {
    id: 'd30',
    label: '30 LEDs/m',
    pitchMm: 33.3,
    wattsPerMetre: 4,
    emitterMm: 1.6,
    emitterWidthMm: 5,
  },
];

export function ledType(id: string): LedType {
  return LED_TYPES.find((type) => type.id === id) ?? LED_TYPES[2];
}

/**
 * Air gap at which individual emitters stop being distinguishable.
 *
 * Light from neighbouring LEDs has to overlap before it reaches the panel. The
 * usual guidance is a gap of at least the emitter pitch; a white, reflective
 * box buys a little, so this uses 0.8.
 */
export const BLEND_RATIO = 0.8;

/**
 * Run spacing at which a lightbox stops showing bands.
 *
 * Each run lights a patch of panel roughly as wide as the gap is deep. Spacing
 * much beyond that leaves visible stripes between the runs.
 */
export const UNIFORMITY_RATIO = 1.5;

export interface LightCheck {
  /** True when the rule is satisfied. */
  ok: boolean;
  /** What the design currently has, in millimetres. */
  actualMm: number;
  /** The value that would satisfy the rule, in millimetres. */
  requiredMm: number;
}

/** Will the individual LEDs blend into a continuous line? */
export function checkBlending(airGapMm: number, type: LedType): LightCheck {
  const required = type.pitchMm * BLEND_RATIO;
  return { ok: airGapMm >= required, actualMm: airGapMm, requiredMm: required };
}

/** Will the runs light the panel evenly, or show bands between them? */
export function checkUniformity(runSpacingMm: number, airGapMm: number): LightCheck {
  const required = airGapMm * UNIFORMITY_RATIO;
  return { ok: runSpacingMm <= required, actualMm: runSpacingMm, requiredMm: required };
}

/**
 * Attenuation length of the panel material, in millimetres.
 *
 * How far light travels into a filament before it has dropped to `1/e`. These
 * are approximate — pigment load varies by brand far more than by colour name —
 * but the ratios between them are what matter for choosing a panel thickness.
 */
export interface PanelMaterial {
  id: string;
  label: string;
  attenuationMm: number;
  /** Shown when a material will not work at all. */
  opaque?: boolean;
}

export const PANEL_MATERIALS: readonly PanelMaterial[] = [
  { id: 'natural', label: 'Natural / clear PETG', attenuationMm: 6 },
  { id: 'white', label: 'White PLA', attenuationMm: 2.6 },
  { id: 'translucent', label: 'Translucent colour', attenuationMm: 3.5 },
  { id: 'opaque', label: 'Opaque or dark', attenuationMm: 0.5, opaque: true },
];

export function panelMaterial(id: string): PanelMaterial {
  return PANEL_MATERIALS.find((material) => material.id === id) ?? PANEL_MATERIALS[1];
}

/**
 * Fraction of light reaching the front through `thickness` of panel.
 *
 * Beer–Lambert, which treats the panel as a uniform absorbing medium. Real
 * filament scatters as well as absorbs, so this understates how much light
 * spreads sideways — but it gets the important behaviour right: relief valleys
 * glow and peaks stay dark, and the falloff with thickness is steep.
 */
export function transmission(thicknessMm: number, material: PanelMaterial): number {
  return Math.exp(-Math.max(0, thicknessMm) / Math.max(material.attenuationMm, 0.05));
}

/** Thickness at which transmission falls to `fraction`. */
export function thicknessForTransmission(fraction: number, material: PanelMaterial): number {
  const clamped = Math.min(Math.max(fraction, 1e-4), 0.999);
  return -Math.log(clamped) * material.attenuationMm;
}

/**
 * Transmission below which a surface stops reading as lit.
 *
 * Like the two ratios above this is a working threshold rather than a measured
 * one. It sits where a panel in a normally lit room stops looking like a lamp
 * and starts looking like a wall with a lamp somewhere behind it.
 */
export const LIT_TRANSMISSION = 0.08;

export interface TransmissionCheck {
  /** Fraction of light reaching the front where the panel is thinnest. */
  brightest: number;
  /** Fraction reaching the front at the deepest point of the relief. */
  darkest: number;
  /** True when even the thinnest point of the panel reads as lit. */
  ok: boolean;
  /** Backing plate at which the thinnest point would read as lit, in millimetres. */
  maxBaseMm: number;
  /** True when the relief peaks carry light too, rather than reading as black. */
  peaksLit: boolean;
  /**
   * Relief depth whose peaks would still read as lit, in millimetres. Zero when
   * the backing plate alone already blocks that much, because then no relief
   * depth fixes it and the plate or the material has to change first.
   */
  maxReliefMm: number;
}

/**
 * Does light actually come out of the front, and where?
 *
 * Two questions, because they have different answers and different fixes. If
 * the thinnest part of the panel is dark the wall does not light at all, and
 * the backing plate or the filament is what changes that. If only the peaks are
 * dark the wall lights and the relief reads as a silhouette — a legitimate
 * look, not a fault, which is why it does not fail the check. It is reported
 * all the same, with the relief depth that would light the whole surface,
 * because "1% at the deepest relief" is otherwise a number you have to
 * translate into "the peaks stay black" yourself.
 */
export function checkTransmission(
  baseThicknessMm: number,
  reliefDepthMm: number,
  material: PanelMaterial,
): TransmissionCheck {
  const base = Math.max(0, baseThicknessMm);
  const relief = Math.max(0, reliefDepthMm);
  const litThickness = thicknessForTransmission(LIT_TRANSMISSION, material);

  return {
    brightest: transmission(base, material),
    darkest: transmission(base + relief, material),
    ok: base <= litThickness,
    maxBaseMm: litThickness,
    peaksLit: base + relief <= litThickness,
    maxReliefMm: Math.max(0, litThickness - base),
  };
}
